import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { SupabaseDatabase } from '../domain/supabase/database.types';

interface SupabaseConfiguration {
  client: SupabaseClient<SupabaseDatabase> | null;
  isConfigured: boolean;
  message: string;
}

const PRODUCTION_SUPABASE_HOSTNAME = 'ialwsdspcwixmzhguqur.supabase.co';

type SupabaseEnvironmentResolution =
  | { status: 'allowed'; url: string; publishableKey: string }
  | {
      status: 'missing' | 'invalid' | 'blocked';
      client: null;
      isConfigured: false;
      message: string;
    };

export const resolveSupabaseEnvironment = (
  urlValue: string | undefined,
  publishableKeyValue: string | undefined,
  isDevelopment: boolean
): SupabaseEnvironmentResolution => {
  const url = urlValue?.trim();
  const publishableKey = publishableKeyValue?.trim();

  if (!url || !publishableKey) {
    return {
      status: 'missing', client: null, isConfigured: false,
      message: 'Configuração do Supabase pendente neste ambiente.'
    };
  }

  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') throw new Error('Unsupported protocol');
    const hostname = parsedUrl.hostname.toLowerCase().replace(/\.$/, '');
    if (isDevelopment && hostname === PRODUCTION_SUPABASE_HOSTNAME) {
      return {
        status: 'blocked',
        client: null,
        isConfigured: false,
        message: 'Desenvolvimento não pode usar o Supabase de produção.'
      };
    }
    return { status: 'allowed', url, publishableKey };
  } catch {
    return {
      status: 'invalid', client: null, isConfigured: false,
      message: 'A configuração do Supabase é inválida neste ambiente.'
    };
  }
};

const createSupabaseConfiguration = (): SupabaseConfiguration => {
  const resolution = resolveSupabaseEnvironment(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    import.meta.env.DEV
  );
  if (resolution.status !== 'allowed') {
    return resolution;
  }

  try {
    return {
      client: createClient<SupabaseDatabase>(resolution.url, resolution.publishableKey, {
        auth: {
          autoRefreshToken: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
          persistSession: true
        }
      }),
      isConfigured: true,
      message: 'Supabase configurado.'
    };
  } catch {
    return {
      client: null,
      isConfigured: false,
      message: 'A configuração do Supabase é inválida neste ambiente.'
    };
  }
};

export const supabaseConfiguration = createSupabaseConfiguration();
